/**
 * UtilityService
 * --------------
 * This service provides common utility functions that are reused across
 * the application. It serves as a catchall for helper methods that don't
 * belong to a specific feature or domain.
 *
 * Example uses:
 * - String formatting and manipulation
 * - Date formatting
 * - General helper functions
 *
 * This service helps reduce code duplication and improves consistency
 * across the project by centralizing commonly used operations.
 */

import toast from 'react-hot-toast';

type MousePosition = {
  x: number;
  y: number;
};

const UtilityService = {
  getApiUrl(): string {
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;

      if (hostname.includes('useassistiv')) {
        return 'https://backend.useassistiv.com/';
      }
    }
    return process.env.NEXT_PUBLIC_API_URL_PRODUCTION || 'https://backend.useassistiv.com';
  },

  getAuroraApiUrl(): string {
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;

      if (hostname.includes('useassistiv')) {
        return 'https://backend.useassistiv.com/aurora/v1/';
      }
    }
    return (
      process.env.NEXT_PUBLIC_AURORA_API_URL_PRODUCTION ||
      'https://backend.useassistiv.com/aurora/v1'
    );
  },

  cleanInvalidWhitespace(input: string) {
    const cleanValue = input.replace(/&nbsp;/g, ' ').replace(/[\s\u00A0]+$/, '');
    return cleanValue;
  },

  getFirstLetterOfNWords(s: string | undefined | null, n: number = 2): string {
    // Return empty string if input is undefined or null
    if (s === undefined || s === null) {
      return '';
    }

    return s
      .split(' ')
      .filter((word) => word.length > 0)
      .slice(0, n)
      .map((word) => word[0])
      .join('');
  },

  formatDate(dateString: string, time: boolean = false): string {
    const date = new Date(dateString);

    return `${time ? `${date.toLocaleTimeString('en-US')}, ` : ''}${date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}`;
  },

  copyToClipboard(value?: string): void {
    navigator.clipboard.writeText(value ?? ('' as string));
    toast.success('Copied to clipboard!', { duration: 2000 });
  },

  /**
   * Removes markdown code fences (```markdown ... ```) from a string if present.
   * The backend occasionally wraps generated content inside a markdown
   * fenced code block. This helper unwraps the content so that callers get
   * plain markdown text ready for rendering.
   *
   * @param content - The possibly wrapped markdown string.
   * @returns The unwrapped markdown string.
   */
  stripMarkdownWrapper(content: string): string {
    if (!content) return content;

    const openingFence = /^```(?:markdown)?\s*\r?\n/;
    const closingFence = /\r?\n?```\s*$/;

    // Only strip if the entire string is wrapped – i.e., starts with opening fence and ends with closing fence
    if (openingFence.test(content) && closingFence.test(content)) {
      const withoutStart = content.replace(openingFence, '');
      const withoutBoth = withoutStart.replace(closingFence, '');
      return withoutBoth.trim();
    }

    // Return original if not properly wrapped
    return content;
  },

  setContentBackground(
    color: string | Array<string> | Array<Array<string>> | 'none',
    opacity?: number,
  ): void {
    const contentDiv = document.getElementById('content_bg');
    if (!contentDiv) return;

    // None: Remove background
    if (color === 'none') {
      contentDiv.style.background = 'none';
    }

    // Single color
    else if (!Array.isArray(color)) {
      contentDiv.style.background = color;
      contentDiv.style.opacity = opacity ? opacity.toString() : '1';
    }

    // One Dimensional array
    else if (Array.isArray(color) && typeof color[0] === 'string') {
      const colorString = `
      linear-gradient(
        to right,
        ${color.map((c, index) => {
          return `\n${c} ${Math.round((index / color.length) * 10000) / 100}%,
        ${c} ${Math.round(((index + 1) / color.length) * 10000) / 100}%`;
        })}
      )`;
      contentDiv.style.background = colorString;
    }

    // 2D Array
    else if (Array.isArray(color) && Array.isArray(color[0])) {
      const colorPairs = color as string[][];
      const col = colorPairs.length;

      const backgrounds = colorPairs.map((pair, i) => {
        const denom = i == 0 ? col : col - 1;
        const left = ((i * 100) / denom).toFixed(2);
        const width = (100 / col).toFixed(2);
        return (
          `linear-gradient(to top, ${pair[0]}, ${pair[1]}) ` +
          `${left}% 0% / ${width}% 100% no-repeat`.trim()
        );
      });

      contentDiv.style.background = backgrounds.join(', ');
    }
  },
  downloadBlob(blob: Blob, fileName: string) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  capitalize(s: string): string {
    return s.charAt(0).toUpperCase() + s.slice(1);
  },

  cleanString(s: string): string {
    return s
      .split('-')
      .map((ss) => ss.charAt(0).toUpperCase() + ss.slice(1))
      .join(' ');
  },

  getInteractiveBackgroundGradientStyle(
    mousePos: MousePosition,
    variant: 'default' | 'demo' = 'default',
  ): React.CSSProperties {
    const viewportWidth = typeof window !== 'undefined' ? Math.max(window.innerWidth || 1, 1) : 1;
    const viewportHeight = typeof window !== 'undefined' ? Math.max(window.innerHeight || 1, 1) : 1;
    const accentVar =
      variant === 'demo'
        ? 'var(--color-brand-auth-accent-pop-demo)'
        : 'var(--color-brand-auth-accent-pop)';

    return {
      background: `radial-gradient(
        at ${40 + (mousePos.x / viewportWidth) * 30}%
        ${60 + (mousePos.y / viewportHeight) * 30}%,
        ${accentVar} 5%,
        var(--color-brand-auth-gradient-mid) 81%,
        var(--color-brand-auth-gradient-end) 100%
      )`,
      transition: 'background-position 0.1s ease-out',
    };
  },

  getInteractiveWarpingGradientStyle(mousePos: MousePosition): React.CSSProperties {
    const viewportWidth = typeof window !== 'undefined' ? Math.max(window.innerWidth || 1, 1) : 1;
    const viewportHeight = typeof window !== 'undefined' ? Math.max(window.innerHeight || 1, 1) : 1;

    return {
      background: `radial-gradient(
        at ${(mousePos.x / viewportWidth) * 20}%
        ${(mousePos.y / viewportHeight) * 10}%,
        rgba(103,155,252,0) 63%,
        color-mix(in srgb, var(--color-brand-auth-warp-mid) 60%, transparent) 70%,
        var(--color-brand-auth-gradient-end) 100%
      )`,
      filter: `hue-rotate(${(mousePos.x / viewportWidth) * 20 - 10}deg) brightness(1.1)`,
      transition: 'filter 0.2s ease-out, background-position 0.1s ease-out',
    };
  },

  getGradientStyle(darkMode: boolean): React.CSSProperties {
    return {
      background: darkMode
        ? 'linear-gradient(to right, color-mix(in srgb, var(--color-brand-highlight) 18%, #000000), color-mix(in srgb, var(--color-brand-mid) 15%, #000000))'
        : 'linear-gradient(to right, color-mix(in srgb, var(--color-brand-highlight) 25%, #fafafa), color-mix(in srgb, var(--color-brand-mid) 20%, #fafafa))',
    };
  },
};

export default UtilityService;
